import MultiTenantCustomerSearch from '@components/CustomerSearch/MultiTenantCustomerSearch';
import { useState } from 'react';
import CustomerDetails from '../CustomerDetails';
import CustomerMfeAdapter from '../CustomerMfeAdapter';
import type { ResolvedCustomer } from '../CustomerMfeAdapter';
import classNames from 'classnames';
import useTasks from '@modules/TasksModule/hooks/useTasks';
import TasksModule from '@modules/TasksModule';
import TaskLoadingOverlay from '@app/layouts/MainLayout/components/TaskLoadingOverlay/TaskLoadingOverlay';
import { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import useFeatureFlag from '@modules/FeatureFlagModule/hooks/useFeatureFlag/useFeatureFlag';

export type SelectedCustomer = {
  tenantId: string;
  customerId: string;
};

/** customer-mfe's resolved-customer round-trip seed (REQ-TXT-ORGID-006 AC-9).
 * Distinct from SelectedCustomer (the legacy non-MFE search's numeric-tenantId
 * shape) — the MFE branch scopes by Guid. */
type MfeSelectedCustomer = {
  orgNodeId: string;
  customerId: string;
};

interface MultiTenantTaskViewProps {
  taskId: string | undefined;
}

/**
 * This component is used to display the customer search and customer details for a multi-tenant task.
 * A task is multi-tenant if it does not have a tenantId associated with the task.
 *
 */
const MultiTenantTaskView = ({ taskId }: MultiTenantTaskViewProps) => {
  const [selectedCustomer, setSelectedCustomer] = useState<
    SelectedCustomer | undefined
  >(undefined);
  const [mfeSelectedCustomer, setMfeSelectedCustomer] = useState<
    MfeSelectedCustomer | undefined
  >(undefined);

  const { taskById } = useTasks();
  const task = taskId ? taskById[taskId] : undefined;
  const isCustomerMfeEnabled = useFeatureFlag(
    FeatureFlagKeys.featureEnableAgentInboxForCalls,
    { defaultValue: false },
  );

  const handleBackClick = () => {
    setSelectedCustomer(undefined);

    if (!task) {
      return;
    }

    TasksModule.setCustomerOnTask(task.id, undefined);
  };

  const handleCustomerSelect = (
    customerId: string,
    customerName: string,
    tenantId: string,
  ) => {
    setSelectedCustomer({ tenantId, customerId });

    if (!task) {
      return;
    }

    TasksModule.setCustomerOnTask(task.id, {
      id: customerId,
      name: customerName,
    });
  };

  const handleMfeCustomerResolved = (customer: ResolvedCustomer) => {
    setMfeSelectedCustomer(
      customer?.orgNodeId
        ? { orgNodeId: customer.orgNodeId, customerId: customer.id }
        : undefined,
    );

    if (!taskId) {
      return;
    }

    TasksModule.setCustomerOnTask(taskId, customer);
  };

  if (isCustomerMfeEnabled) {
    return (
      // flex-grow-1: this view is a sibling in InboxPage's flex-direction:column
      // Stack — align-items:stretch only affects the cross (horizontal) axis there,
      // so without flex-grow this div (and customer-mfe's height:100% shell inside
      // it) collapses to its content's height instead of filling the page.
      // No p-x-3/p-y-2 here — customer-mfe owns its own internal spacing and
      // should fill this pane edge to edge, unlike the native search/details forms.
      <div className={classNames('of-y-auto', 'd-f', 'flex-grow-1')}>
        <TaskLoadingOverlay taskId={taskId}>
          {/* mfeSelectedCustomer re-seeds the adapter on re-render once resolved
              — this is the load-bearing half of the AC-9 round-trip: without
              it, a resolved cross-tenant task silently falls back to Mode 1
              (all-tenants search) on the next render instead of staying
              tenant-scoped. */}
          {taskId && (
            <CustomerMfeAdapter
              orgNodeId={mfeSelectedCustomer?.orgNodeId}
              customerId={mfeSelectedCustomer?.customerId}
              campaignId={task?.attributes.campaign?.id}
              callSid={task?.attributes.sessionSid}
              onCustomerChanged={handleMfeCustomerResolved}
            />
          )}
        </TaskLoadingOverlay>
      </div>
    );
  }

  return (
    <div className={classNames('p-x-3', 'p-y-2', 'of-y-auto', 'd-f')}>
      <TaskLoadingOverlay taskId={taskId}>
        {selectedCustomer && (
          <CustomerDetails
            customerId={selectedCustomer.customerId}
            tenantId={selectedCustomer.tenantId}
            onBackClick={handleBackClick}
          />
        )}
        <MultiTenantCustomerSearch
          show={!selectedCustomer}
          onSelectCustomer={handleCustomerSelect}
        />
      </TaskLoadingOverlay>
    </div>
  );
};

export default MultiTenantTaskView;
